import { api } from "../api.ts";
import { BRAND, icon, MICROSOFT } from "../icons.ts";
import { $, $$, errorText, esc, initials, load } from "../ui.ts";
import { go, signedIn, type ViewContext } from "../main.ts";

const ENTRA_ERRORS: Record<string, string> = {
  not_registered: "Tu cuenta de Microsoft no está dada de alta en Vera. Pide el alta a administración.",
  suspended: "Tu cuenta está suspendida. Habla con administración.",
  tenant: "Esa cuenta no pertenece a la empresa. Entra con tu cuenta corporativa.",
  expired: "El inicio de sesión ha caducado. Vuelve a intentarlo.",
  cancelled: "Has cancelado el inicio de sesión.",
  not_configured: "El inicio de sesión con Microsoft no está configurado.",
};

export function mount(root: HTMLElement, ctx: ViewContext) {
  root.innerHTML = `
    <main class="login" id="main" tabindex="-1">
      <div class="login-card">
        <span class="brand">${BRAND}<span>Vera</span></span>
        <div style="display:grid;gap:12px">
          <h1>Tu asistente personal, con tu permiso.</h1>
          <p class="lead">Escríbele o mándale una nota de voz. Se ocupa del correo, la agenda, las reservas y los viajes, y te pide confirmación antes de pagar, reservar o escribir a nadie en tu nombre.</p>
        </div>
        <ul>
          <li>${icon("shield-check", "sm")}<span>Nunca ve contraseñas ni tarjetas.</span></li>
          <li>${icon("lock", "sm")}<span>Topes de gasto que no puede saltarse, y un botón para pararlo todo.</span></li>
          <li>${icon("history", "sm")}<span>Todo lo que hace queda registrado.</span></li>
        </ul>
        <section aria-labelledby="who" style="display:grid;gap:10px">
          <div data-code hidden class="field">
            <label for="access-code">Código de acceso</label>
            <input id="access-code" name="accessCode" autocomplete="off" autocapitalize="none" spellcheck="false" aria-describedby="code-hint">
            <span class="hint" id="code-hint">Te lo da quien te enseña la demo. Se recuerda en este dispositivo.</span>
          </div>
          <h2 id="who" style="font-size:15px" data-demo-only>Entrar en la demostración como</h2>
          <div data-users></div>
          <div data-error></div>
        </section>
      </div>
      <footer class="footer"><span><strong>Demostración</strong> · datos ficticios · en producción se entra con la cuenta de Microsoft de la empresa</span></footer>
    </main>`;
  const box = $(root, "[data-users]");
  void load(box, {
    fetch: () => api.loginOptions(),
    placeholder: `<div class="skeleton" aria-busy="true" aria-label="Cargando"><i class="tall"></i><i class="tall"></i></div>`,
    render: ({ users, accessCodeRequired, mode }) => {
      if (mode === "entra") {
        $(root, "[data-demo-only]").hidden = true;
        const reason = ctx.route.query.get("error");
        if (reason) $(root, "[data-error]").innerHTML = `<div class="error-box" role="alert">${icon("circle-alert", "sm")}<span>${esc(ENTRA_ERRORS[reason] ?? "No se ha podido iniciar sesión con Microsoft. Vuelve a intentarlo.")}</span></div>`;
        box.innerHTML = `<a class="btn primary large wide btn-microsoft" href="/api/v1/auth/login" data-entra>${MICROSOFT}<span>Entrar con Microsoft</span></a>
          <p class="ink-3" style="font-size:13px;text-align:center;margin-top:10px">Con tu cuenta de la empresa. Si no puedes entrar, pide el alta a administración.</p>`;
        $(box, "[data-entra]").addEventListener("click", (e) => { const a = e.currentTarget as HTMLElement; a.setAttribute("aria-busy", "true"); a.querySelector("span")!.textContent = "Conectando con Microsoft…"; });
        $(root, ".footer").innerHTML = "<span>Acceso con la cuenta corporativa de Microsoft</span>";
        return;
      }
      const codeBox = $(root, "[data-code]");
      const codeInput = $<HTMLInputElement>(root, "#access-code");
      codeBox.hidden = !accessCodeRequired;
      try { codeInput.value = localStorage.getItem("accessCode") ?? ""; } catch { /* storage blocked */ }
      box.innerHTML = users.length ? `<div class="list">${users.map((u) => `
        <button type="button" class="row link" data-user="${u.id}" style="width:100%;border:0;background:none;text-align:left;cursor:pointer;color:inherit;font:inherit">
          <span class="avatar" aria-hidden="true">${esc(initials(u.name))}</span>
          <span class="row-main"><strong>${esc(u.name)}</strong><span class="secondary">${esc(u.email)}${u.role === "admin" ? " · administración" : ""}</span></span>
          ${icon("chevron-right")}</button>`).join("")}</div>` : `<p class="ink-2">No hay usuarios activos.</p>`;
      $$<HTMLButtonElement>(box, "[data-user]").forEach((b) => b.addEventListener("click", async () => {
        $$<HTMLButtonElement>(box, "[data-user]").forEach((x) => { x.disabled = true; });
        b.querySelector(".secondary")!.textContent = "Entrando…";
        try {
          const code = accessCodeRequired ? codeInput.value.trim() : undefined;
          signedIn(await api.demoLogin(b.dataset.user!, code));
          try { if (code) localStorage.setItem("accessCode", code); } catch { /* storage blocked */ }
          go("#/chat");
        } catch (err) {
          $(root, "[data-error]").innerHTML = `<div class="error-box" role="alert">${icon("circle-alert", "sm")}<span><strong>No se ha podido entrar.</strong> ${esc(errorText(err))}</span></div>`;
          $$<HTMLButtonElement>(box, "[data-user]").forEach((x) => { x.disabled = false; });
          b.querySelector(".secondary")!.textContent = users.find((u) => u.id === b.dataset.user)?.email ?? "";
          if (accessCodeRequired) { codeInput.setAttribute("aria-invalid", "true"); codeInput.focus(); }
        }
      }));
    },
  });
}
